import { useParams } from "react-router-dom";
import { createGraphQLClientWithMiddleware } from "../../../services/graphqlClient";
import {
  SalesReturnFindUniqueQuery,
  useSalesReturnFindUniqueQuery,
} from "../../../generated/sales";
import { IPdfInfo } from "../../../types/global";
import { formatDate } from "../../../utils/Date";
import { renderAddressString } from "../../../utils/Formatter/Global";
import { Box, CircularProgress, Typography } from "@mui/material";
import SalesPdfLayout from "../../../components/UI/SalesPdf/SalesPdfLayout";
import SalesItemListPdfTable from "../../../components/Table/Pdf/SalesItemList";
import { ISalesItemList } from "../../../types/Sales";
import {
  ApprovalTemplateFindByDocumentTypeQuery,
  useApprovalTemplateFindByDocumentTypeQuery,
} from "../../../generated/general";
import { usePDFMultiplePage } from "../../../hooks/use-pdf-multiple-page";
import { useReactToPrint } from "react-to-print";
import { ReactInstance, useRef } from "react";
import CustomizedButton from "../../../components/Custom/CustomizedButton";
import { IContactPerson } from "../../../types/Contact/contact";

type Props = {
  name: string;
};

const SalesReturnPdf = ({ name }: Props) => {
  const { id } = useParams();
  const componentRef = useRef<ReactInstance | null>(null);
  const graphQLClient = createGraphQLClientWithMiddleware("sales");
  const graphQLClientGeneral = createGraphQLClientWithMiddleware("general");

  const { data, isLoading } =
    useSalesReturnFindUniqueQuery<SalesReturnFindUniqueQuery>(graphQLClient, {
      uniqueInput: {
        id: id ? parseInt(id) : undefined,
      },
    });

  const { data: approvalTemplates, isLoading: isApprovalLoading } =
    useApprovalTemplateFindByDocumentTypeQuery<ApprovalTemplateFindByDocumentTypeQuery>(
      graphQLClientGeneral,
      {
        documentType: "sales_return",
      }
    );

  const { paginatedItems, startNumbers } = usePDFMultiplePage(
    data?.SalesReturnFindUnique, //data
    "sales_return", // document type
    141, // name and description row width
    700, // max height without footer
    500, // max height with footer
    226.484, // left header minimum height
    226.484, // right header minimum height
    undefined, // left footer minimum height (you don't need to specify this if the document doesn't have remark and tags)
    undefined // right footer minimum height (you don't need to specify this if the document doesn't have summary)
  );

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: data?.SalesReturnFindUnique.unique_id,
  });

  const highlightSection: IPdfInfo = {
    headers: ["เลขที่เอกสาร", "วันที่ออกเอกสาร"],
    info: [
      data?.SalesReturnFindUnique.unique_id || "-",
      data?.SalesReturnFindUnique.issue_date
        ? formatDate(data?.SalesReturnFindUnique.issue_date)
        : "-",
      // data?.SalesReturnFindUnique.due_date
      //   ? formatDate(data?.SalesReturnFindUnique.issue_date)
      //   : "-",
    ],
  };

  const leftSection: IPdfInfo = {
    headers: [
      "ลูกค้า",
      "ที่อยู่",
      "เลขประจำตัวผู้เสียภาษี",
      "เบอร์โทรศัพท์@โทรสาร",
      "อีเมล",
    ],
    info: [
      `${data?.SalesReturnFindUnique.customer_unique_id} ${data?.SalesReturnFindUnique.customer_details.name}`,
      renderAddressString(
        data?.SalesReturnFindUnique.customer_details.billing_address
      ),
      data?.SalesReturnFindUnique.customer_details.identity_no || "-",
      `${data?.SalesReturnFindUnique.customer_details.phone || "-"}@${
        data?.SalesReturnFindUnique.customer_details.fax || "-"
      }`,
      data?.SalesReturnFindUnique.customer_details.email || "-",
    ],
  };

  const salesPersonFirstName =
    (data?.SalesReturnFindUnique.related_user_list &&
      data?.SalesReturnFindUnique.related_user_list.length > 0 &&
      data.SalesReturnFindUnique.related_user_list[0].first_name) ||
    "-";

  const salesPersonLastname =
    (data?.SalesReturnFindUnique.related_user_list &&
      data?.SalesReturnFindUnique.related_user_list.length > 0 &&
      data.SalesReturnFindUnique.related_user_list[0].last_name) ||
    "";
  const salePerson = salesPersonFirstName + " " + salesPersonLastname;

  const contactPerson: IContactPerson =
    data?.SalesReturnFindUnique.contact_person_list &&
    data?.SalesReturnFindUnique.contact_person_list.length > 0 &&
    data?.SalesReturnFindUnique.contact_person_list[0];

  const contactPersonName = contactPerson
    ? `${contactPerson.title_name}${contactPerson.first_name} ${contactPerson.last_name}`
    : "-";

  const contactPersonPhone = contactPerson
    ? contactPerson.contact_channel_list.find(
        (channel) => channel.contact_channel_type === "phone"
      )?.contact_channel_info || "-"
    : "-";

  const rightSection: IPdfInfo = {
    headers: [
      // "อ้างอิงถึง",
      "พนักงานขาย",
      // "เครดิต (วัน)",
      "หมายเลขอ้างอิง",
      "ผู้ติดต่อ",
      "เบอร์โทรศัพท์",
    ],
    info: [
      // data?.SalesReturnFindUnique.reference_document_list
      //   ?.map((doc) => doc.document_unique_id)
      //   .join(", ") || "-",
      salePerson || "-",
      // `${data?.SalesReturnFindUnique.credit_day?.toString()} วัน` || "-",
      data?.SalesReturnFindUnique.reference_no || "-",
      contactPersonName,
      contactPersonPhone,
    ],
  };

  const allPages = paginatedItems.length;

  if (id && (isLoading || isApprovalLoading)) {
    return (
      <Box
        sx={{
          height: "calc(100dvh - 176px)",
          marginRight: "260px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box maxWidth={1650}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h5">{name}</Typography>
          <Typography>{`เลขที่เอกสาร ${data?.SalesReturnFindUnique.unique_id}`}</Typography>
        </Box>
        <Box>
          <CustomizedButton
            fullWidth
            title="พิมพ์เอกสาร"
            variant="outlined"
            onClick={handlePrint}
          />
        </Box>
      </Box>
      <Box display={"flex"} justifyContent={"center"}>
        <Box ref={componentRef} sx={{ m: "auto" }}>
          {paginatedItems.map((item, index) => (
            <SalesPdfLayout
              highlightSection={highlightSection}
              leftSection={leftSection}
              rightSection={rightSection}
              name={"ใบรับคืน"}
              multiplePage={paginatedItems.length > 1}
              page={index + 1}
              allPages={allPages}
              data={data?.SalesReturnFindUnique}
              summary={index === paginatedItems.length - 1}
              salePerson={salePerson}
              totalAmount={
                data?.SalesReturnFindUnique.net_amount
                  ? data?.SalesReturnFindUnique.net_amount
                  : 0
              }
              documentType="sales_return"
              hideAdditionalDiscount={false}
              showPaymentSection={false} //Change to true to show payment section below footer
              approvalTemplates={
                approvalTemplates?.ApprovalTemplateFindByDocumentType || []
              }
            >
              <SalesItemListPdfTable
                itemList={(item as ISalesItemList[]) || []}
                startNumber={startNumbers[index]}
                isLastPage={index === paginatedItems.length - 1}
                lastTable={
                  paginatedItems[paginatedItems.length - 1].length === 0
                    ? index === paginatedItems.length - 2
                    : index === paginatedItems.length - 1
                }
                totalAmount={
                  data?.SalesReturnFindUnique.net_amount
                    ? data?.SalesReturnFindUnique.net_amount
                    : 0
                }
                hasItems={item.length > 0}
              />
            </SalesPdfLayout>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default SalesReturnPdf;
